"use client";
import { API_URL } from "@/lib/config";

import { useEffect, useState } from "react";
import Link from "next/link";
import { getReviewsApiPanoramasPanoramaIdReviewsGet, ReviewResponse, getCollectionsApiCollectionsGet, CollectionResponse, addPanoramaToCollectionApiCollectionsCollectionIdPanoramasPanoramaIdPost } from "@/api-client";
import { Star, MessageSquare, Bookmark, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/features/auth";
import SocialBar from "./SocialBar";

export default function ReviewOverlay({
  panoramaId,
  creatorName = "Demo User",
  isVerifiedCreator = true,
  initialLikeCount = 0,
  initialCommentCount = 0,
  onOpenComments,
}: {
  panoramaId: string,
  creatorName?: string,
  isVerifiedCreator?: boolean,
  initialLikeCount?: number,
  initialCommentCount?: number,
  onOpenComments: () => void,
}) {
  const { user } = useAuth();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  // Phase 4: Collections Save logic
  const [showCollections, setShowCollections] = useState(false);
  const [collections, setCollections] = useState<CollectionResponse[]>([]);
  const [isSavingCol, setIsSavingCol] = useState<string | null>(null);
  const [savedSuccessCol, setSavedSuccessCol] = useState<string | null>(null);

  // Utilize the SDK native fetcher mapping straight to FastAPI GET endpoint
  useEffect(() => {
    async function load() {
      try {
        const { data } = await getReviewsApiPanoramasPanoramaIdReviewsGet({ path: { panorama_id: panoramaId }});
        if (data) setReviews(data);
      } catch (err) {
        console.error("Failed to load reviews:", err);
      }
    }
    load();
  }, [panoramaId]);

  const loadCollections = async () => {
    if (collections.length === 0) {
      try {
        const apiBase = API_URL;
        // Use fetch directly so we can pass panorama_id for the contains_panorama flag
        const res = await fetch(`${apiBase}/api/collections?panorama_id=${panoramaId}`, { credentials: "include" });
        if (res.ok) {
          const data: CollectionResponse[] = await res.json();
          setCollections(data);
          // Pre-mark collections that already contain this panorama as "saved"
          data.forEach((c) => {
            if ((c as any).contains_panorama) {
              setSavedSuccessCol(String(c.id));
            }
          });
        }
      } catch(err) { console.error(err); }
    }
    setShowCollections(!showCollections);
  };

  const saveToCollection = async (collectionId: string) => {
    setIsSavingCol(collectionId);
    try {
      await addPanoramaToCollectionApiCollectionsCollectionIdPanoramasPanoramaIdPost({
        path: { collection_id: collectionId, panorama_id: panoramaId },
        query: { sort_order: 0 }
      });
      setSavedSuccessCol(collectionId);
      // Update collections list to reflect the new contains_panorama state
      setCollections((prev) =>
        prev.map((c) => String(c.id) === collectionId ? { ...c, contains_panorama: true } as any : c)
      );
    } catch(err) {
      console.error(err);
    } finally {
      setIsSavingCol(null);
    }
  };


  return (
    <>
      {/* ── Mobile collapsed pill (hidden on md+) ── */}
      <div
        className={`${mobileExpanded ? "hidden" : "md:hidden"} absolute left-3 z-[150] animate-in slide-in-from-bottom-4 fade-in duration-400`}
        style={{ bottom: "calc(var(--sai-bottom, 0px) + 5rem)" }}
      >
        {/* SocialBar floats centered above the pill */}
        <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-10">
          <SocialBar
            panoramaId={panoramaId}
            initialLikeCount={initialLikeCount}
            initialCommentCount={initialCommentCount}
            onOpenComments={onOpenComments}
          />
        </div>
        <button
          onClick={() => setMobileExpanded(true)}
          className="flex items-center gap-1.5 px-3 py-1 bg-canvas/55 backdrop-blur-xl border border-ink/12 rounded-full text-ink/75 shadow-lg"
        >
          <span className="text-[11px] font-medium">Captured by</span>
          <span className="text-[11px] font-bold text-ink truncate max-w-[120px]">{creatorName}</span>
          {isVerifiedCreator && (
            <svg className="w-3 h-3 text-blue-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          )}
        </button>
      </div>

      {/* ── Mobile backdrop when expanded ── */}
      {mobileExpanded && (
        <div
          className="md:hidden fixed inset-0 z-[149]"
          onClick={() => setMobileExpanded(false)}
        />
      )}

      {/* ── Full panel: always visible on md+, toggled on mobile ── */}
      <div
        className={`absolute left-3 z-[150] w-72 md:w-80 ${
          mobileExpanded ? "block" : "hidden md:block"
        }`}
        style={{ bottom: "calc(var(--sai-bottom, 0px) + 5rem)" }}
      >
        {/* SocialBar floats centered above the panel's top edge */}
        <div className="absolute -top-14 left-0 right-0 flex justify-center z-10">
          <SocialBar
            panoramaId={panoramaId}
            initialLikeCount={initialLikeCount}
            initialCommentCount={initialCommentCount}
            onOpenComments={onOpenComments}
          />
        </div>

        {/* Panel visual container */}
        <div className="bg-canvas/50 backdrop-blur-md border border-ink/10 rounded-xl md:rounded-2xl p-3 md:p-5 text-ink shadow-2xl flex flex-col max-h-[60vh] overflow-y-auto animate-in slide-in-from-bottom-10 fade-in duration-500">

      {/* ── Phase 4: Gamification Badge Header ── */}
      <div className="mb-3 md:mb-4 pb-2 md:pb-3 border-b border-ink/10 flex items-center justify-between shrink-0">
        <div className="flex flex-col">
          <span className="text-xs text-tone-400 font-semibold tracking-wide uppercase">Captured By</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Link
              href={`/creators/${encodeURIComponent(creatorName)}`}
              className="font-bold text-ink text-sm hover:text-blue-400 transition-colors"
            >
              {creatorName}
            </Link>
            {isVerifiedCreator && (
              <div className="bg-blue-500/20 text-blue-400 rounded-full p-0.5" title="Verified Creator">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Phase 4: Save to Collection Button */}
        <div className="relative">
          <button 
            onClick={loadCollections}
            className="p-2 bg-ink/5 hover:bg-ink/10 rounded-full transition-colors text-indigo-300 hover:text-indigo-200"
            title="Save to Collection"
          >
            <Bookmark className="w-4 h-4" />
          </button>
          
          {showCollections && (
            <div className="absolute top-10 right-0 w-52 bg-tone-900 border border-ink/10 rounded-xl shadow-2xl p-2 z-[200] max-h-52 overflow-y-auto">
              <h4 className="text-xs font-bold text-tone-400 uppercase tracking-wider mb-2 px-2">Save to...</h4>
              {collections.length === 0 ? (
                <p className="text-xs text-tone-500 px-2 pb-1">No collections. Create one in Studio.</p>
              ) : (
                collections.map((c: CollectionResponse) => {
                  const alreadySaved = savedSuccessCol === String(c.id) || (c as any).contains_panorama;
                  return (
                    <button
                      key={String(c.id)}
                      onClick={() => !alreadySaved && saveToCollection(String(c.id))}
                      className={`w-full text-left p-2 rounded-lg transition-colors text-sm flex justify-between items-center gap-2 ${
                        alreadySaved
                          ? "opacity-70 cursor-default"
                          : "hover:bg-ink/5"
                      }`}
                    >
                      <span className="truncate">{c.name}</span>
                      {isSavingCol === String(c.id) && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
                      {alreadySaved && isSavingCol !== String(c.id) && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 shrink-0" aria-label="Already in this collection" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <h3 className="text-xs md:text-sm font-bold flex items-center gap-2 mb-2 md:mb-3 drop-shadow-sm text-tone-200 shrink-0">
        <MessageSquare className="w-3 h-3 md:w-4 md:h-4 text-blue-400" /> Explorer Feedback
      </h3>

      {/* Review Scroll Area */}
      <div className="overflow-y-auto flex-1 min-h-[3rem] space-y-2 md:space-y-3 mb-2 md:mb-4 pr-1 md:pr-2 scrollbar-thin scrollbar-thumb-white/20">
        {reviews.length === 0 ? (
           <div className="h-24 flex items-center justify-center text-center">
              <p className="text-sm text-tone-400 font-medium">No reviews yet. Be the first to chart this area!</p>
           </div>
        ) : (
           reviews.map((r: ReviewResponse) => (
             <div key={String(r.id)} className="bg-ink/10 rounded-xl p-3 text-sm border border-ink/5">
                <div className="flex justify-between items-start mb-2">
                   <div className="flex gap-0.5 text-yellow-400">
                     {Array.from({ length: 5 }).map((_, i) => (
                       <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'fill-current' : 'opacity-20'}`} />
                     ))}
                   </div>
                   <span className="text-[10px] text-tone-400">
                      {new Date(r.created_at).toLocaleDateString()}
                   </span>
                </div>
                <p className="text-tone-200 leading-relaxed">{r.comment || "Left a rating."}</p>
             </div>
           ))
        )}
      </div>

        </div>{/* end panel visual container */}
      </div>{/* end panel wrapper */}
    </>
  );
}
